// Payments-tab advanced table's row detail panel ("View details" equivalent
// from the old card UI) — one payment's full chronological event history:
// every delivery attempt, link created, paid/marked-paid event, expiry, and
// the Stripe ref line. Lazily fetches the payment's checkout sessions on
// mount (DataGridPro only mounts a detail panel's content once the row is
// expanded, so this naturally satisfies "fetch on expand" without extra
// wiring), backed by the same sessionsCache the actions cell/columns use.
import PropTypes from 'prop-types';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/Iconify';
import usePaymentSessions from './usePaymentSessions';
import { buildPaymentTimelineEvents, formatDate, formatAppointmentSummaryLine } from './paymentPresentation';
import { derivePaymentCardStatus } from './paymentCardStatus';

// Chip-less inline status word for the detail-panel header line — mirrors
// PaymentsTable's AppointmentCell chip labels but as plain text in
// parentheses ("... (Booked)") since this header is a single text line, not
// a cell with room for a separate chip.
const APPOINTMENT_STATUS_WORD = {
    BOOKED: 'Booked',
    ATTENDED: 'Attended',
    CANCELLED: 'Cancelled',
    FAILED_TO_ATTEND: 'FTA',
};

// A single Timeline-tab-styled event row: tinted-circle icon dot + connecting
// line (mirrors Activities.jsx's feed rows) with a title/timestamp header
// line and optional muted caption / red error sub-line.
export function PaymentTimelineEvent({ event, isLast }) {
    return (
        <Box sx={{ position: 'relative', display: 'flex', pb: isLast ? 0 : 1.25 }} data-testid={event.deliveryId ? `delivery-row-${event.deliveryId}` : undefined}>
            {!isLast && (
                <Box sx={{ position: 'absolute', left: 11, top: 24, bottom: 0, width: '1px', bgcolor: 'rgba(145,158,171,0.16)' }} />
            )}
            <Box sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: `${event.color}1F`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                zIndex: 1,
            }}>
                <Iconify icon={event.icon} sx={{ width: 13, height: 13, color: event.color }} />
            </Box>
            <Box sx={{ ml: 1.25, flex: 1, minWidth: 0, pt: '1px' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>
                        {event.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {formatDate(event.timestamp, 'DD MMM, HH:mm')}
                    </Typography>
                </Stack>
                {event.caption && (
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.25 }}>
                        {event.caption}
                    </Typography>
                )}
                {event.error && (
                    <Typography sx={{ fontSize: '0.6875rem', color: 'error.main', mt: 0.25 }}>
                        {event.error}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

PaymentTimelineEvent.propTypes = {
    event: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        deliveryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        icon: PropTypes.string,
        color: PropTypes.string,
        title: PropTypes.string,
        timestamp: PropTypes.string,
        caption: PropTypes.string,
        error: PropTypes.string,
    }).isRequired,
    isLast: PropTypes.bool,
};

PaymentTimelineEvent.defaultProps = {
    isLast: false,
};

export default function PaymentTimelinePanel({ payment, practiceId, leadId, treatmentId, width }) {
    const status = derivePaymentCardStatus(payment);
    const isLinkPayment = ['awaiting', 'expired', 'failed'].includes(status);
    const { sessions, sessionsLoading } = usePaymentSessions(payment, { practiceId, leadId, treatmentId, eager: true });
    const events = buildPaymentTimelineEvents(payment, sessions);

    // A DataGridPro detail-panel row spans the grid's FULL scrollable content
    // width, which can be wider than what's actually visible in a narrow host
    // (a lead-detail tab, not a full report page). Pinning this content to
    // the left edge and clamping it to the visible viewport width (measured
    // by the parent table) keeps the right-aligned timestamps and the
    // "Link expires" date on-screen without requiring horizontal scroll.
    const stickySx = Number.isFinite(width)
        ? { position: 'sticky', left: 0, width, maxWidth: width, boxSizing: 'border-box' }
        : {};

    const appointmentSummary = payment?.appointment_summary;
    const appointmentStatusWord = appointmentSummary ? APPOINTMENT_STATUS_WORD[appointmentSummary.status] : null;

    return (
        <Box sx={{ p: 2, pl: 5, bgcolor: '#fafbfc', ...stickySx }} data-testid={`payment-detail-panel-${payment?.id}`}>
            {appointmentSummary && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }} data-testid={`payment-detail-appointment-${payment?.id}`}>
                    For {formatAppointmentSummaryLine(appointmentSummary)}{appointmentStatusWord ? ` (${appointmentStatusWord})` : ''}
                </Typography>
            )}
            {sessionsLoading && <CircularProgress size={18} />}
            {!sessionsLoading && events.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                    {isLinkPayment ? 'No checkout sessions yet.' : 'No payment link has been created for this payment yet.'}
                </Typography>
            )}
            {!sessionsLoading && events.length > 0 && (
                <Stack data-testid={`session-history-${payment?.id}`}>
                    {events.map((event, idx) => (
                        <PaymentTimelineEvent key={event.id} event={event} isLast={idx === events.length - 1} />
                    ))}
                </Stack>
            )}
            {status === 'paid' && payment?.payment_intent_ref && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                    Stripe ref {payment.payment_intent_ref}
                </Typography>
            )}
        </Box>
    );
}

PaymentTimelinePanel.propTypes = {
    payment: PropTypes.object.isRequired,
    practiceId: PropTypes.any,
    leadId: PropTypes.any,
    treatmentId: PropTypes.any,
    width: PropTypes.number,
};

PaymentTimelinePanel.defaultProps = {
    practiceId: null,
    leadId: null,
    treatmentId: null,
    width: null,
};
